import { GripVertical } from 'lucide-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import * as ResizablePrimitive from 'react-resizable-panels';

import { cn } from '@heidi/utils';

import {
  resizableHandleClasses,
  resizableHandleGripClasses,
  resizablePanelContentClasses,
  resizablePanelGroupClasses,
} from './resizable.styles';
import type {
  ImperativePanelHandle,
  ResizableHandleProps,
  ResizablePanelGroupProps,
  ResizablePanelProps,
} from './resizable.types';

const toPercentString = (
  value: number | string | undefined
): number | string | undefined => (typeof value === 'number' ? `${value}%` : value);

// Best-effort percentage extraction for imperative clamping. Returns 0 for
// non-percent units (px/rem/etc.) — in that case the wrapper skips clamping
// and v4's own validation takes over.
const parseMinPercent = (value: number | string | undefined): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '' || trimmed.endsWith('%')) {
      const n = parseFloat(trimmed);
      return Number.isFinite(n) ? n : 0;
    }
  }
  return 0;
};

const ResizablePanelGroup = ({
  direction,
  orientation,
  className,
  ...props
}: ResizablePanelGroupProps) => {
  const resolvedOrientation = orientation ?? direction ?? 'horizontal';
  return (
    <ResizablePrimitive.Group
      orientation={resolvedOrientation}
      data-orientation={resolvedOrientation}
      className={cn(resizablePanelGroupClasses, className)}
      {...props}
    />
  );
};

const ResizablePanel = forwardRef<ImperativePanelHandle, ResizablePanelProps>(
  function ResizablePanel(
    {
      defaultSize,
      minSize,
      maxSize,
      collapsedSize,
      onResize,
      onCollapse,
      onExpand,
      className,
      // v4 removed `order`; accept and discard so existing call-sites compile.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      order,
      ...props
    },
    ref
  ) {
    const panelRef = useRef<ResizablePrimitive.PanelImperativeHandle>(null);

    // Track the latest minSize prop so the imperative resize() can clamp
    // below-min requests upward (v1 behaviour) instead of letting v4 collapse
    // the panel. Consumers like useResizableSideBars compute a dynamic pixel
    // minimum that may exceed the stored width, and they call resize(stored)
    // expecting v1's clamp-to-min — v4 would silently collapse instead.
    const minSizeRef = useRef<number | string | undefined>(minSize);
    minSizeRef.current = minSize;

    useImperativeHandle(
      ref,
      () => ({
        resize: (size: number) => {
          const panel = panelRef.current;
          if (!panel) return;
          if (size <= 0) {
            panel.collapse();
            return;
          }
          // Defer to the next microtask so v4's own prop-sync effects (which
          // update maxSize/minSize constraints when the shouldShow props flip)
          // run before we try to expand + resize. Without this defer, a
          // sequence of collapse-then-resize across a React render boundary
          // leaves v4 stuck at size 0 because its internal constraint cache
          // is still from the previous commit.
          queueMicrotask(() => {
            const p = panelRef.current;
            if (!p) return;
            const minPct = parseMinPercent(minSizeRef.current);
            const target = Math.max(size, minPct);
            if (p.isCollapsed()) {
              p.expand();
            }
            p.resize(`${target}%`);
          });
        },
        collapse: () => panelRef.current?.collapse(),
        expand: () => panelRef.current?.expand(),
        isCollapsed: () => panelRef.current?.isCollapsed() ?? false,
        getSize: () => panelRef.current?.getSize().asPercentage ?? 0,
      }),
      []
    );

    const handleResize = useCallback(
      (
        panelSize: ResizablePrimitive.PanelSize,
        _id: string | number | undefined,
        prev: ResizablePrimitive.PanelSize | undefined
      ) => {
        onResize?.(panelSize.asPercentage);
        if (prev) {
          // Compare against the configured collapsed threshold — a non-zero
          // collapsedSize (e.g. 5%) means the panel reports that size when
          // collapsed, not 0, so a strict === 0 check would never fire.
          const threshold = parseMinPercent(collapsedSize);
          const wasCollapsed = prev.asPercentage <= threshold;
          const isCollapsed = panelSize.asPercentage <= threshold;
          if (!wasCollapsed && isCollapsed) onCollapse?.();
          if (wasCollapsed && !isCollapsed) onExpand?.();
        }
      },
      [onResize, onCollapse, onExpand, collapsedSize]
    );

    return (
      <ResizablePrimitive.Panel
        panelRef={panelRef}
        defaultSize={toPercentString(defaultSize)}
        minSize={toPercentString(minSize)}
        maxSize={toPercentString(maxSize)}
        collapsedSize={toPercentString(collapsedSize)}
        onResize={handleResize}
        // Lands on the content wrapper — see `resizablePanelContentClasses`.
        className={cn(resizablePanelContentClasses, className)}
        {...props}
      />
    );
  }
);

const ResizableHandle = ({
  withHandle,
  className,
  onDragging,
  ...props
}: ResizableHandleProps) => {
  const handleRef = useRef<HTMLDivElement>(null);
  // The drag-start signal must come from pointer capture, not pointerdown:
  // react-resizable-panels uses a hit region wider than the handle element, so a
  // real press often lands on a neighbouring node and the handle's own
  // `pointerdown` (React or native) never fires — only the later events, which
  // the library retargets here via `setPointerCapture`. `gotpointercapture` /
  // `lostpointercapture` fire on the handle exactly when the library starts/ends
  // a drag, regardless of where the press landed.
  useEffect(() => {
    const el = handleRef.current;
    if (!el || !onDragging) return;
    const onCapture = () => onDragging(true);
    const onRelease = () => onDragging(false);
    el.addEventListener('gotpointercapture', onCapture);
    el.addEventListener('lostpointercapture', onRelease);
    return () => {
      el.removeEventListener('gotpointercapture', onCapture);
      el.removeEventListener('lostpointercapture', onRelease);
    };
  }, [onDragging]);

  return (
    <ResizablePrimitive.Separator
      elementRef={handleRef}
      className={cn(resizableHandleClasses, className)}
      {...props}
    >
      {withHandle && (
        <div className={resizableHandleGripClasses}>
          <GripVertical className="size-2.5" />
        </div>
      )}
    </ResizablePrimitive.Separator>
  );
};

export { ResizableHandle, ResizablePanel, ResizablePanelGroup };
